import { useCallback, useEffect, useState } from 'react'

import { navigate, useLocation, useParams } from '@redwoodjs/router'
import { useQuery, useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useTaskModalContext } from 'src/context/TaskModalContext'
import { GET_TASKS, EXPORT_TASKS_AS_CSV } from 'src/graphql/tasks.graphql'
import { TaskStatus } from 'src/graphql/types/tasks'
import IconAdd from 'src/icons/IconAdd'
import IconDownload from 'src/icons/IconDownload'

import TaskFilterBar from './components/TaskFilterBar'
import TasksTable from './components/TasksTable'

// When no status filter is applied, default to active work.
const DEFAULT_STATUSES = [TaskStatus.NOT_STARTED, TaskStatus.IN_PROGRESS]

const TasksPage = () => {
  const { id } = useParams()
  const { search } = useLocation()
  const { currentUser } = useAuth()
  const { handleOpen, registerRefetch } = useTaskModalContext()

  //Filters
  const [selectedTaskTypes, setSelectedTaskTypes] = useState([])
  const [selectedTaskPriorities, setSelectedTaskPriorities] = useState([])
  // Default to "my tasks" on first load when no explicit assignee filter is chosen.
  const currentEmployeeId =
    currentUser?.active?.employeeId ?? currentUser?.employeeId
  const [selectedRecruiters, setSelectedRecruiters] = useState<string[]>(
    () => (currentEmployeeId ? [currentEmployeeId] : [])
  )
  const [searchText, setSearchText] = useState('')
  // Default to active work (Not Started + In Progress) — shown pre-selected in
  // the Status filter.
  const [selectedStatuses, setSelectedStatuses] = useState([
    ...DEFAULT_STATUSES,
  ])

  // Falling back to the default keeps the list sensible if the user clears the
  // status filter entirely.
  const effectiveStatuses =
    selectedStatuses.length > 0 ? selectedStatuses : DEFAULT_STATUSES

  // Pagination state, seeded from and synced to the URL (?page & ?perPage)
  const [page, setPage] = useState(
    () => Number(new URLSearchParams(window.location.search).get('page')) || 1
  )
  const [perPage, setPerPage] = useState(
    () =>
      Number(new URLSearchParams(window.location.search).get('perPage')) || 20
  )

  const updatePaginationInUrl = (nextPage: number, nextPerPage: number) => {
    const params = new URLSearchParams(window.location.search)
    params.set('page', String(nextPage))
    params.set('perPage', String(nextPerPage))
    navigate(`?${params.toString()}`, { replace: true })
  }

  // Keep pagination state in sync with the URL for browser back/forward and any
  // external navigation that changes ?page / ?perPage. Functional updates keep
  // this a no-op when our own updatePaginationInUrl just wrote the same values.
  useEffect(() => {
    const params = new URLSearchParams(search)
    const urlPage = Number(params.get('page')) || 1
    const urlPerPage = Number(params.get('perPage')) || 20
    setPage((prev) => (prev === urlPage ? prev : urlPage))
    setPerPage((prev) => (prev === urlPerPage ? prev : urlPerPage))
  }, [search])

  // Server-side pagination: DnTable hands back the offset/limit to fetch.
  const handleLoadPage = ({ offset, limit }) => {
    const nextPage = Math.floor(offset / limit) + 1
    setPage(nextPage)
    setPerPage(limit)
    updatePaginationInUrl(nextPage, limit)
  }

  // Wrap a filter setter so it also resets to page 1 in the same render — this
  // avoids a transient fetch that pairs the new filters with a stale page.
  const onFilterChange = (setter) => (value) => {
    setter(value)
    setPage(1)
    updatePaginationInUrl(1, perPage)
  }

  useEffect(() => {
    if (id) {
      handleOpen({ id })
    }
  }, [id, handleOpen])

  const {
    data,
    loading,
    refetch: refetchTasks,
  } = useQuery(GET_TASKS, {
    variables: {
      pageInfo: {
        limit: perPage,
        offset: (page - 1) * perPage,
      },
      filters: {
        status: effectiveStatuses,
        type: selectedTaskTypes,
        priority: selectedTaskPriorities,
        searchText: searchText.trim(),
        assigneeIds:
          selectedRecruiters?.length > 0 ? [...selectedRecruiters] : undefined,
      },
    },
  })

  const tasks = data?.tasks?.items ?? []
  const totalCount = data?.tasks?.totalCount ?? 0

  // If the current page falls past the end of the result set (a stale ?page, or
  // filters/completions shrinking the total), clamp back to the last real page
  // so the table never sits on an empty page.
  useEffect(() => {
    if (loading || totalCount === 0) return
    const lastPage = Math.max(1, Math.ceil(totalCount / perPage))
    if (page > lastPage) {
      setPage(lastPage)
      updatePaginationInUrl(lastPage, perPage)
    }
    // updatePaginationInUrl is stable for these inputs; deps kept minimal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, totalCount, perPage, page])

  const handleRefetch = useCallback(() => {
    refetchTasks()
  }, [refetchTasks])

  useEffect(() => {
    registerRefetch(handleRefetch)
  }, [registerRefetch, handleRefetch])

  // Export functionality
  const [exportTasks, { loading: exportLoading }] = useMutation(
    EXPORT_TASKS_AS_CSV,
    {
      onCompleted: (data) => {
        if (data?.exportTasksAsCsv) {
          // Create a blob and download the CSV
          const blob = new Blob([data.exportTasksAsCsv], { type: 'text/csv' })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `tasks-export-${
            new Date().toISOString().split('T')[0]
          }.csv`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
          toast('Tasks exported successfully', 'success')
        }
      },
      onError: (error) => {
        toast(`Export failed: ${error.message}`, 'error')
      },
    }
  )

  const handleExportTasks = () => {
    // Export matches what the table is showing (same filters incl. status).
    const filters = {
      type: selectedTaskTypes,
      priority: selectedTaskPriorities,
      searchText: searchText.trim(),
      assigneeIds:
        selectedRecruiters?.length > 0 ? [...selectedRecruiters] : undefined,
      status: effectiveStatuses,
    }

    exportTasks({
      variables: {
        filters,
      },
    })
  }

  const totalTasks = totalCount
  return (
    <div className="flex h-full w-full flex-col gap-3 overflow-hidden pb-5">
      <div className="flex flex-row items-center justify-between gap-2 px-5">
        <div className="flex flex-col">
          <div className="flex flex-col text-lg font-semibold text-doubleNickel-gray-900">
            Tasks
          </div>
          <div className="text-sm text-doubleNickel-gray-600">
            {totalTasks} task{totalTasks === 1 ? '' : 's'}
          </div>
        </div>
        <div className="flex flex-row items-center gap-2">
          <TaskFilterBar
            selectedTaskTypes={selectedTaskTypes}
            setSelectedTaskTypes={onFilterChange(setSelectedTaskTypes)}
            selectedTaskPriorities={selectedTaskPriorities}
            setSelectedTaskPriorities={onFilterChange(
              setSelectedTaskPriorities
            )}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={onFilterChange(setSelectedStatuses)}
            selectedRecruiters={selectedRecruiters}
            setSelectedRecruiters={onFilterChange(setSelectedRecruiters)}
            searchText={searchText}
            setSearchText={onFilterChange(setSearchText)}
          />
          <DNButton
            variant="secondary"
            onClick={handleExportTasks}
            loading={exportLoading}
            disabled={exportLoading}
          >
            <IconDownload className="h-3 w-3 fill-none stroke-doubleNickel-gray-700" />
            Export CSV
          </DNButton>
          <DNButton
            variant="primary"
            onClick={() => {
              handleOpen()
            }}
          >
            <IconAdd className="h-3 w-3 fill-none stroke-doubleNickel-white" />
            Create Task
          </DNButton>
        </div>
      </div>
      <div className="min-h-0 flex-1 px-5">
        <TasksTable
          tasks={tasks}
          loading={loading}
          refetch={handleRefetch}
          page={page}
          perPage={perPage}
          totalCount={totalCount}
          onLoadPage={handleLoadPage}
          onRowClick={(task) => {
            if (task.status === TaskStatus.COMPLETED) {
              handleOpen({ id: task.taskId })
            } else {
              navigate(`/tasks/${task.taskId}`)
            }
          }}
        />
      </div>
    </div>
  )
}

export default TasksPage
